import { useState, useRef, useContext, useEffect } from "react";
import { FluentButton } from "../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import { Tooltip } from "@fluentui/react-components";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { LoginContext } from "../../../../services/api/authentication-api";
import { useHistory } from "react-router-dom";
import { FluentInput } from "../../../../components/FluentUI/Fluent-Input";
import PropTypes from "prop-types";
import {
  getDealTrackerSummaryColumns,
  getInitialDetailState,
  saveDetailStateToStorage,
  clearDetailStateFromStorage,
  getFlattenedData,
  getMyDealDetailColumns,
  handleDealTrackerExport,
  mapMyDealSummaryExportData,
  mapDealTrackerSummaryExportData,
} from "../levfin-helper";
import "../levfin-junior-banker/levfin-time-log.scss";
import LevfinDealTrackerDetail from "./levfin-deal-tracker-detail";
import LevfinDealTrackerSummaryGrid from "./levfin-deal-tracker-summary";

const LevfinDealTracker = ({ items, isStafferView }) => {
  const user = useContext(LoginContext);
  const [showDetails, setShowDetails] = useState(true);
  const [detailState, setDetailState] = useState(() => getInitialDetailState(user?.userID, "levfinDealTracker"));
  const [data, setData] = useState(items.Deals || []);
  const [searchValue, setSearchValue] = useState("");
  const [summaryItem, setSummaryItem] = useState(items);
  const [expandClass, setExpandClass] = useState("");
  const _detailExport = useRef(null);
  const _summaryExport = useRef(null);
  const searchValueRef = useRef(searchValue);
  const history = useHistory();
  const canCreate = user?.currentUser?.privileges?.["Projects"]?.Add ?? false;
  const canEdit = user?.currentUser?.privileges?.["Projects"]?.Edit ?? false;
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value.trimStart());
  };

  useEffect(() => {
    searchValueRef.current = searchValue;
    if (searchValue?.trim()) {
      clearDetailStateFromStorage(user?.userID, "levfinDealTracker");
    }
  }, [searchValue]);


  const handleModifySearchFocus = () => {
    const input = document.getElementById("levfin-search-input");
    if (input) {
      input.focus();
    }
  };

  const handleDetailStateChange = (newState) => {
    const userId = user?.userID;
    setDetailState(newState);
    saveDetailStateToStorage(userId, newState, searchValueRef.current, "levfinDealTracker");
  };

  const handleItemChange = (e) => {
    const { field, value, dataItem } = e;
    setData((prev) =>
      prev.map((row) =>
        row.DealId === dataItem.DealId ? { ...row, [field]: value } : row
      )
    );
  };

  const hasAnyFilter =
    detailState.filter &&
    Array.isArray(detailState.filter.filters) &&
    detailState.filter.filters.length > 0;

  const canReset = detailState.sort.length > 0 || hasAnyFilter;
  const resetDataState = () => {
    setSearchValue("");
    const resetState = { sort: [], filter: null };
    setDetailState(resetState);
    clearDetailStateFromStorage(user?.userID, "levfinDealTracker");
  };

  const updateSummaryNumbers = (filteredData) => {
    const counts = {};

    // Initialize all category options with 0 (mirrors categoryOptions seeding in C#)
    if (items.CategoryOptions?.length) {
      items.CategoryOptions.forEach((option) => {
        if (option?.trim()) {
          counts[option.trim()] = 0;
        }
      });
    }

    // Count deals per GroupName (mirrors the foreach over deals in C#)
    filteredData.forEach((deal) => {
      const key = deal.GroupName?.trim() || "Uncategorized";
      counts[key] = (counts[key] || 0) + 1;
    });

    // Convert to array of { Key, Value } pairs to match KeyValuePair<string, int>[]
    const countByCategory = Object.entries(counts).map(([key, value]) => ({
      Key: key,
      Value: value,
    }));

    setSummaryItem((prevItems) => ({
      ...prevItems,
      Total: filteredData.length,
      NumberOfDeals: countByCategory,
    }));
  };


  return (
    <>
      <div className={`levfin-time-log ${expandClass} dashboard-container-shadow`}>
        <div className="widget-header">
          <div className="header-left">
            <div className="main-title-badge-row">
              <div className="main-title">Deal Tracker</div>
              <FluentInput
                id="levfin-search-input"
                placeholder="Search"
                type="text"
                value={searchValue}
                onChange={handleSearchChange}
                componentStyle="search-input"
                contentBefore={<FluentUiIcon name="SearchRegular" size={14} />}
                noKey
              />
            </div>
            <div className="sub-title">GIB/Leveraged Finance</div>
          </div>
          <div className="header-right">
            <Tooltip content="Reset Filters and Sorting">
              <FluentButton
                appearance="secondaryWithOutline"
                icon={
                  <FluentUiIcon
                    name="ArrowResetRegular"
                    color={canReset ? "#0F6CBD" : "#A0A0A0"}
                    size={20}
                    disabled={!canReset}
                  />
                }
                disabled={!canReset}
                onClick={resetDataState}
              />
            </Tooltip>

            <Tooltip content="Export Data">
              <FluentButton
                appearance="secondaryWithOutline"
                icon={
                  <FluentUiIcon
                    name="ArrowDownloadRegular"
                    color="#0F6CBD"
                    size={20}
                  />
                }
                onClick={() =>
                  handleDealTrackerExport({
                    summaryRef: _summaryExport,
                    detailRef: _detailExport,
                    summaryData: mapMyDealSummaryExportData(summaryItem),
                    detailData: getFlattenedData(data),
                    categoryOptions: items?.CategoryOptions || [],
                  })
                }
              />
            </Tooltip>

            <Tooltip content="Expand Widget">
              <span>
                <FluentButton
                  appearance="secondaryWithOutline"
                  icon={
                    <FluentUiIcon
                      name={
                        expandClass === "widgetExpanded"
                          ? "FullScreenMinimizeFilled"
                          : "FullScreenMaximizeFilled"
                      }
                      color={"#0F6CBD"}
                      size={20}
                    />
                  }
                  onClick={() => setExpandClass(expandClass === "" ? "widgetExpanded" : "")}
                />
              </span>
            </Tooltip>
            <FluentButton
              text="Create Deal"
              appearance="secondaryWithOutline"
              disabled={!canCreate}
              icon={
                <FluentUiIcon
                  name="FolderAddRegular"
                  color={canCreate ? "#0F6CBD" : "#A0A0A0"}
                  size={20}
                />
              }
              onClick={() => {
                if (canCreate) {
                  history.push({
                    pathname: "/projects/create",
                    state: { fromDashboard: true },
                  });
                }
              }}
            />
          </div>
        </div>

        <ExcelExport data={mapDealTrackerSummaryExportData(summaryItem)} ref={_summaryExport}>
          {getDealTrackerSummaryColumns(items?.CategoryOptions)}
        </ExcelExport>
        <ExcelExport data={getFlattenedData(data)} ref={_detailExport}>
          {getMyDealDetailColumns(data)}
        </ExcelExport>

        <LevfinDealTrackerSummaryGrid
          summaryItem={summaryItem}
        />
        {showDetails && (
          <>
            <LevfinDealTrackerDetail
              data={data}
              categoryOptions={items.CategoryOptions}
              emptyCategories={items.EmptyCategories}
              onItemChange={handleItemChange}
              canEdit={canEdit}
              canCreate={canCreate}
              detailState={detailState}
              onDetailStateChange={handleDetailStateChange}
              searchValue={searchValue}
              resetDataState={resetDataState}
              refineSearch={handleModifySearchFocus}
              updateSummaryNumbers={updateSummaryNumbers}
              isStafferView={isStafferView}
            />
            <div
              tabIndex={0}
              style={{ width: 0, height: 0, overflow: "hidden" }}
              onFocus={() => {
                const btn = document.getElementById("tb-submit-btn");
                if (btn) btn.focus();
              }}
            />
          </>
        )}

      </div>
    </>
  );
};

LevfinDealTracker.propTypes = {
  items: PropTypes.shape({
    Deals: PropTypes.arrayOf(PropTypes.object).isRequired,
    CategoryOptions: PropTypes.arrayOf(PropTypes.string),
    EmptyCategories: PropTypes.array,
  }).isRequired,
};

export default LevfinDealTracker;
